/**
 * safety.ts — the PUBLIC clinical-safety read.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: public clinical-safety
 * page (2026-08-20).
 *
 * WHY THIS IS A SEPARATE ENDPOINT AND NOT `prices.ts`'s plan-options read.
 * `getPlanOptions` is gated — it is the priced, post-eligibility catalogue, and
 * it carries money. This read is deliberately UNAUTHENTICATED, because the page
 * it feeds has to work from a link in a dispatch email, from a clinician's
 * bookmark, and from a phone that has never had a session on this origin. The
 * two must never become one call: widening the priced read to public is how a
 * clinic's commercial configuration ends up on an open URL, and narrowing this
 * one to authed is how the safety link in an email starts bouncing people to a
 * sign-in they cannot complete.
 *
 * WHAT MAKES A PUBLIC READ OF POM CONTENT LAWFUL HERE (and it is a narrow
 * basis, so do not widen it casually): what comes back is risk information and
 * nothing else — no price, no availability, no offer. The moment an offer rides
 * on this response, the page it renders stops being information and becomes an
 * advertisement for a prescription-only medicine.
 *
 * R28: no medication name and no clinical prose is written in this file. Every
 * word of it arrives on the wire.
 */
import { get } from './client';
import type { PublicClinicSafetyResponse } from './types';

/**
 * The gateway route this call depends on.
 *
 * Kept as a named constant rather than inlined because it is a CROSS-REPO
 * coupling. The gateway owns the external path and forwards it:
 *
 *   GET /v1/prices/public/medication-safety?clinicSlug=
 *     → prices-service GET /v1/public/medication-safety
 *
 * Note the SINGLE `prices` segment. Every other prices call in this repo goes
 * through the doubled `/v1/prices/prices/…` prefix, because those routes live
 * under the service's own `prices` controller — this one does not, and copying
 * the doubled prefix out of `api/prices.ts` earns a 404 that looks like an
 * outage. Verified against the gateway's `publicMedicationSafety` handler and
 * the prices-service `v1/public` controller rather than assumed.
 */
export const PUBLIC_SAFETY_PATH = '/v1/prices/public/medication-safety';

/**
 * GET the public safety content for every medication a clinic offers.
 *
 * NO `auth: true`. Sending a bearer here would be worse than pointless: the
 * route does not want one, and a 401 on an authed call clears the shared
 * session — so a signed-in patient opening this page in a second tab could find
 * themselves signed out of the first one.
 */
export function getPublicClinicSafety(clinicSlug: string): Promise<PublicClinicSafetyResponse> {
  return get(`${PUBLIC_SAFETY_PATH}?clinicSlug=${encodeURIComponent(clinicSlug)}`);
}
